import { useRef } from "react";
import { Button, Flex, Text } from "@theorgchartdev/storybook";
import { CurrentPlanFieldsModal } from "@/appComponents/Blocks/Modals/PlanFieldsModal";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { appstr } from "@coreapp/utils/appstr";
import { enumOverrideType, enumObjectTypes, kDefaultPlanFields } from "@coreapp/constants";
import RecordEditorModal from "@/appComponents/Blocks/Modals/RecordEditorModal/RecordEditorModal";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";
import { DEFAULT_POSITION_PLAN_FIELDS } from "@coreapp/CoreApp/position-management.constants";

export const basicEmployeeEmployeeFields = kDefaultPlanFields;
export const basicEmployeePositionFields = DEFAULT_POSITION_PLAN_FIELDS.holder;

export const PlanRecordEditorModal = () => {
  const {
    recordEditorContextValue: { theData, showRecordEditor, onSaveRecordEdit, onCloseRecordEditor }
  } = usePlanManagerContext();

  const fieldsHandleRef = useRef(null);
  const { canManageFields } = getPlanPermissions({ useCurrentPlan: true });

  let recordData = theData[enumObjectTypes.holder];

  if (!showRecordEditor) return null;

  const wsMgr = GetWSMgr();
  const planMgr = wsMgr.GetPlanMgr();
  if (!planMgr) return null;

  const cloneOverride = planMgr.CloneDeltas(recordData.override);
  // We only want holder data so we pass specific field type
  const additionalFields = planMgr.fPlanInfo.fields?.holder ?? [];

  // Determine initial tab based on the record data
  const getInitialTab = () => {
    if (recordData.isMismatch) {
      return 1;
    }

    if (recordData.type === enumOverrideType.comment) {
      return 2;
    }

    return 0;
  };

  const doc = GetDoc();
  const basicFields = doc?.IsPositionDocument()
    ? basicEmployeePositionFields
    : basicEmployeeEmployeeFields;

  return (
    <RecordEditorModal
      initTab={getInitialTab()}
      data={theData}
      dataTestIdPrefix="plan-record-editor-"
      show={showRecordEditor}
      manager={planMgr}
      overrideObj={recordData.override}
      cloneOverride={cloneOverride}
      dbObj={recordData.dataObj}
      type={recordData.type}
      branchAdd={recordData.branchAdd}
      initChanges={recordData.initChanges}
      basicFields={basicFields}
      additionalFields={additionalFields}
      onSave={onSaveRecordEdit}
      onClose={onCloseRecordEditor}
      showEffectiveDate={planMgr?.GetUseEffectiveDates()}
      isPlanEdit
      additionalFieldsActionNode={
        <AdditionalFieldsControls
          fieldsHandleRef={fieldsHandleRef}
          planMgr={planMgr}
          canManageFields={canManageFields}
        />
      }
      additionalFieldsEmptyNode={
        <AdditionalFieldsEmptyNotice
          fieldsHandleRef={fieldsHandleRef}
          planMgr={planMgr}
          canManageFields={canManageFields}
        />
      }
      otherHeaderProps={{ modifier: "grey" }}
      overlayProps={{
        showOverlay: true
      }}
      showCommentsTab={true}
    />
  );
};

const AdditionalFieldsEmptyNotice = props => {
  const { fieldsHandleRef } = props;

  if (!props?.canManageFields) {
    return (
      <Flex direction={"column"} gap={"8px"}>
        <Text size={"small"} colorModifier={"grey"}>
          {appstr("No additional fields added")}
        </Text>
        <Text size={"small"} colorModifier={"grey"}>
          {appstr("Contact plan owner to add additional fields")}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction={"row"} justifyContent={"center"}>
      <Button
        dataTestId={"plan-record-editor-manage-fields"}
        modifier={"secondary"}
        size={"small"}
        onClick={() => {
          fieldsHandleRef.current?.handleOpen();
        }}
      >
        {appstr("Click here to add additional fields")}
      </Button>
    </Flex>
  );
};

const AdditionalFieldsControls = props => {
  const { fieldsHandleRef, planMgr } = props;

  let controlsNode = null;

  const additionalFields = planMgr.fPlanInfo.fields?.holder ?? [];

  if (additionalFields.length > 0) {
    controlsNode = (
      <Flex direction={"row"} justifyContent={"center"}>
        <Button
          dataTestId={"plan-record-editor-manage-fields"}
          size={"medium"}
          modifier={"secondary"}
          disabled={!props?.canManageFields}
          onClick={() => {
            fieldsHandleRef.current?.handleOpen();
          }}
        >
          {appstr("Manage plan fields")}
        </Button>
      </Flex>
    );
  }

  return (
    <>
      {controlsNode}
      <CurrentPlanFieldsModal handleRef={fieldsHandleRef} planMgr={planMgr} />
    </>
  );
};

export default PlanRecordEditorModal;
